import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import ScanditScanner from "../barcode/ScanditScanner/ScanditScanner";

export default function SerialNumbersEditorModal({
  open,
  onClose,
  title = "Add Serial Number",
  initialSerials = [],
  fieldLabel = "Serial Number",
  addButtonLabel = "+ Add Serial Number",
  scanTooltip = "Scan Barcode",
  onSave,
}) {
  const [serials, setSerials] = useState([]);
  const [openScanIndex, setOpenScanIndex] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const normalized = (initialSerials || []).map(v => String(v || "").trim()).filter(Boolean);
    setSerials(normalized.length ? normalized : [""]);
    setOpenScanIndex(null);
    setSaving(false);
  }, [open, initialSerials]);

  const canSave = useMemo(() => serials.some(v => String(v || "").trim()), [serials]);

  const updateRow = (index, value) => {
    setSerials(prev => prev.map((v, i) => (i === index ? value : v)));
  };

  const removeRow = index => {
    setSerials(prev => (prev.length <= 1 ? [""] : prev.filter((_, i) => i !== index)));
  };

  const addRow = () => {
    setSerials(prev => {
      const nextIndex = prev.length;
      setOpenScanIndex(nextIndex);
      return [...prev, ""];
    });
  };

  const handleSave = async () => {
    const next = serials.map(v => String(v || "").trim()).filter(Boolean);
    setSaving(true);
    try {
      await onSave(next);
      onClose();
    } catch (_error) {
      // The caller owns user-facing error messaging.
    } finally {
      setSaving(false);
    }
  };

  const handleDialogClose = (_event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="xs"
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 450,
        },
      }}
    >
      <DialogTitle
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pr: 6,
          pb: 1,
        }}
      >
        <Typography
          component="span"
          sx={{
            color: "common.black",
            fontSize: "1.2rem",
            fontWeight: 500,
            textAlign: "center",
          }}
        >
          {title}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            color: "text.secondary",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {serials.map((value, index) => (
            <Box key={`serial-row-${index}`} display="flex" gap={1} alignItems="flex-start">
              <TextField
                fullWidth
                size="small"
                label={`${fieldLabel} ${index + 1}`}
                value={value}
                onChange={event => updateRow(index, event.target.value)}
                autoComplete="off"
                autoFocus={index === 0}
                sx={{ backgroundColor: "white" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip arrow title={scanTooltip}>
                        <IconButton size="small" onClick={() => setOpenScanIndex(index)}>
                          <QrCodeScannerIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip arrow title="Remove">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => removeRow(index)}
                    disabled={serials.length <= 1 && !String(value || "").trim()}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}

          <Box>
            <Button variant="outlined" onClick={addRow}>
              {addButtonLabel}
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
          Save
        </Button>
      </DialogActions>

      {openScanIndex !== null && (
        <ScanditScanner
          onDetected={code => {
            updateRow(openScanIndex, code);
            setOpenScanIndex(null);
          }}
          onClose={() => setOpenScanIndex(null)}
        />
      )}
    </Dialog>
  );
}
